import { EventType, FailureContext, generateEventTypeMapDefaults, WorkflowEventEventTypeMap, WorkflowEventMetaDataMapWithDefaults } from '@aiwyn/shared';

/**
 * Flow 8b — Auto-Payment Plans (Autopay).
 * Covers create / update / deactivate for auto-payment plans.
 *
 * Each async op follows the SUBMITTED + SUCCEEDED + FAILED triad. Terminals carry
 * `operationDurationMs`; FAILED metadata extends `FailureContext`. Create and update share
 * `AUTO_PAYMENT_PLAN_SUBMITTED` as the operation-start marker (the dialog handles both modes);
 * deactivate's `AUTO_PAYMENT_PLAN_DEACTIVATE_SUBMITTED` doubles as funnel-entry — the delete
 * dialog fires the mutation as soon as the user confirms, so there's no earlier UI gesture
 * worth a separate marker.
 */
export enum AutoPaymentPlanWorkflowEvent {
  AUTO_PAYMENT_PLAN_CREATE_STARTED = 'AUTO_PAYMENT_PLAN_CREATE_STARTED',
  AUTO_PAYMENT_PLAN_PAYMENT_METHOD_SELECTED = 'AUTO_PAYMENT_PLAN_PAYMENT_METHOD_SELECTED',
  AUTO_PAYMENT_PLAN_SUBMITTED = 'AUTO_PAYMENT_PLAN_SUBMITTED',
  AUTO_PAYMENT_PLAN_CREATE_SUCCEEDED = 'AUTO_PAYMENT_PLAN_CREATE_SUCCEEDED',
  AUTO_PAYMENT_PLAN_CREATE_FAILED = 'AUTO_PAYMENT_PLAN_CREATE_FAILED',
  AUTO_PAYMENT_PLAN_UPDATE_STARTED = 'AUTO_PAYMENT_PLAN_UPDATE_STARTED',
  AUTO_PAYMENT_PLAN_UPDATE_SUCCEEDED = 'AUTO_PAYMENT_PLAN_UPDATE_SUCCEEDED',
  AUTO_PAYMENT_PLAN_UPDATE_FAILED = 'AUTO_PAYMENT_PLAN_UPDATE_FAILED',
  AUTO_PAYMENT_PLAN_DEACTIVATE_SUBMITTED = 'AUTO_PAYMENT_PLAN_DEACTIVATE_SUBMITTED',
  AUTO_PAYMENT_PLAN_DEACTIVATE_SUCCEEDED = 'AUTO_PAYMENT_PLAN_DEACTIVATE_SUCCEEDED',
  AUTO_PAYMENT_PLAN_DEACTIVATE_FAILED = 'AUTO_PAYMENT_PLAN_DEACTIVATE_FAILED',
  AUTO_PAYMENT_PLAN_CANCELLED = 'AUTO_PAYMENT_PLAN_CANCELLED',
}

export type AutoPaymentPlanWorkflowEventMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  AutoPaymentPlanWorkflowEvent,
  {
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_PAYMENT_METHOD_SELECTED]: { paymentMethodType: string };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_SUBMITTED]: { dayOfMonth: string; maxAmount?: number };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CREATE_SUCCEEDED]: { planId: string; operationDurationMs: number };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CREATE_FAILED]: FailureContext & { operationDurationMs: number };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_UPDATE_STARTED]: { planId: string };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_UPDATE_SUCCEEDED]: { planId: string; operationDurationMs: number };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_UPDATE_FAILED]: FailureContext & { planId: string; operationDurationMs: number };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_DEACTIVATE_SUBMITTED]: { planId: string };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_DEACTIVATE_SUCCEEDED]: { planId: string; operationDurationMs: number };
    [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_DEACTIVATE_FAILED]: FailureContext & { planId: string; operationDurationMs: number };
  }
>;

export const AutoPaymentPlanEventEventTypeMap: WorkflowEventEventTypeMap<AutoPaymentPlanWorkflowEvent> = {
  ...generateEventTypeMapDefaults(AutoPaymentPlanWorkflowEvent, EventType.USER),
  [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CREATE_SUCCEEDED]: EventType.SYSTEM,
  [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_UPDATE_SUCCEEDED]: EventType.SYSTEM,
  [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_DEACTIVATE_SUCCEEDED]: EventType.SYSTEM,
  [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CREATE_FAILED]: EventType.ERROR,
  [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_UPDATE_FAILED]: EventType.ERROR,
  [AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_DEACTIVATE_FAILED]: EventType.ERROR,
};
